import { blockerSentence, problemCountLabel, problemSentence } from '@/lib/protocol-labels';
import type { ProtocolProblem, ProtocolProductionBlocker } from '@matter_extractor/shared';
import { Alert } from '@pms-digital/design-system';

/**
 * Ce que la boîte « Enregistrer une nouvelle version » refuse, avant le clic
 * (refonte modulaire §7.1; maquette `editeur-protocole.html`, `saveDialogHTML`).
 *
 * Two refusals and two alerts, because they are not the same refusal:
 *
 *   - the PROBLEMS are what the version itself is refused on (§7.1: a step
 *     without a turn cap, an objective without a criterion, a jump or a fallback
 *     aimed at a step that has gone). They hold whether or not the production
 *     box is ticked, so their alert is `warning` and always present when there
 *     are any.
 *   - the BLOCKERS are what « Passer en production » refuses on top, and only
 *     then: a criterion cited under `production` that has none, a retired one,
 *     one the base no longer holds. Their alert is `danger` and appears with
 *     the tick.
 *
 * Both lists are computed by the SAME functions the server refuses with
 * (`protocolProblems` and `productionBlockers` in `@matter_extractor/shared`),
 * which is the whole argument of that module's head: two implementations of one
 * rule set drift, and the drift shows as a box that lets a click through to a
 * 409 nobody expected.
 *
 * This component only RENDERS them. Whether the confirm button is closed is the
 * caller's answer through `renderBlockers`, because the box owns its own
 * checkbox and this file does not.
 */

export interface ProtocolSaveBlockersProps {
  /** What the version is refused on. Empty renders no `warning` alert. */
  problems: readonly ProtocolProblem[];
  /** What the tick is refused on. Empty renders no `danger` alert. */
  blockers: readonly ProtocolProductionBlocker[];
}

export function ProtocolSaveBlockers({ problems, blockers }: ProtocolSaveBlockersProps) {
  if (problems.length === 0 && blockers.length === 0) return null;

  return (
    <div className="space-y-3">
      {problems.length === 0 ? null : (
        <div data-testid="protocole-boite-problemes">
          <Alert
            tone="warning"
            title={`La version ne peut pas être enregistrée : ${problemCountLabel(problems.length)}.`}
          >
            <span className="mt-1.5 block space-y-1">
              {problems.map((problem) => (
                <span key={`${problem.kind}-${problemSentence(problem)}`} className="block">
                  · {problemSentence(problem)}
                </span>
              ))}
            </span>
            {/* The draft SURVIVES the refusal, and the box says so: a refusal that
              did not name what it left behind would read as work lost. */}
            <span className="mt-1.5 block text-caption text-gray-600 leading-4">
              Le brouillon est conservé. Corrigez, puis réessayez.
            </span>
          </Alert>
        </div>
      )}

      {blockers.length === 0 ? null : (
        <div data-testid="protocole-boite-blocages">
          <Alert tone="danger" title="Refusé en production :">
            <span className="mt-1.5 block space-y-1">
              {blockers.map((blocker) => (
                <span
                  key={`${blocker.kind}-${blocker.criterionId}-${blocker.stepId}-${blocker.objectiveName ?? ''}`}
                  className="block"
                >
                  · {blockerSentence(blocker)}
                </span>
              ))}
            </span>
            <span className="mt-1.5 block text-caption text-gray-600 leading-4">
              Une référence en production doit résoudre. Passez d’abord ces critères en production,
              ou forcez leur version développement dans l’objectif.
            </span>
          </Alert>
        </div>
      )}
    </div>
  );
}
